import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    PawPrint, Plus, PencilSimple, ShieldCheck, ArrowUpRight,
    QrCode, DotsThreeVertical, Trash, MapPin, Siren, ShieldStar
} from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import CareRemindersCard from "@/components/CareRemindersCard";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { finderUrl } from "@/lib/petUtils";

export default function PetsPage() {
    const { user } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [pets, setPets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);

    const load = async () => {
        setLoadError(false);
        try {
            const { data } = await api.get("/pets");
            setPets(data);
        } catch (_) {
            setLoadError(true);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const retry = () => { setLoading(true); load(); };

    const remove = async (pet) => {
        setConfirmingId(null);
        try {
            await api.delete(`/pets/${pet.id}`);
            load();
        } catch (_) { /* ignore */ }
    };

    return (
        <PageShell
            testId="pets-page"
            maxWidth="max-w-5xl"
            kicker={t("pets_page.hero_kicker")}
            title={t("pets_page.hero_h")}
            sub={t("pets_page.hero_sub")}
            actions={pets.length > 0 && (
                <button
                    onClick={() => nav("/pets/new")}
                    data-testid="pets-add-btn"
                    className="btn-primary text-sm w-full sm:w-auto"
                >
                    <Plus size={16} /> <span className="ms-1.5">{t("pets_page.add_pet")}</span>
                </button>
            )}
        >
            {pets.length > 0 && (
                <TrustBand count={pets.length} t={t} />
            )}

            {/* PawLink Care reminders — quick, non-medical reminders per pet. */}
            {pets.length > 0 && (
                <div className="mt-6">
                    <CareRemindersCard pets={pets} />
                </div>
            )}

            {/* Local Walks removed from Pets — it lives inside /community. */}

            {loading ? (
                <PetSkeletons />
            ) : loadError ? (
                <PetsErrorState onRetry={retry} t={t} />
            ) : pets.length === 0 ? (
                <EmptyPets onCreate={() => nav("/pets/new")} t={t} />
            ) : (
                <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="pets-grid">
                    {pets.map((p) => (
                        <PetCard
                            key={p.id}
                            pet={p}
                            confirming={confirmingId === p.id}
                            onConfirm={() => setConfirmingId(p.id)}
                            onCancelConfirm={() => setConfirmingId(null)}
                            onDelete={() => remove(p)}
                            t={t}
                        />
                    ))}
                </div>
            )}
        </PageShell>
    );
}

/* ------------- Trust band ------------- */
function TrustBand({ count, t }) {
    return (
        <section
            data-testid="pets-trust-band"
            className="relative mb-6 sm:mb-8 rounded-3xl overflow-hidden border border-paw-forest/15 dark:border-paw-terra/25 bg-paw-forest text-white dark:bg-paw-forestDark"
        >
            <div aria-hidden className="absolute -top-16 -end-10 w-56 h-56 rounded-full bg-paw-terra/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -start-10 w-64 h-64 rounded-full bg-white/8 blur-3xl" />
            <div className="relative p-5 sm:p-7 flex items-start gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/12 backdrop-blur grid place-items-center flex-shrink-0">
                    <ShieldCheck size={26} weight="fill" className="text-paw-terra" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-paw-terra/95 font-semibold">
                        {t("pets_page.status_active")}
                    </p>
                    <h2 className="mt-0.5 font-display text-xl sm:text-2xl leading-tight">
                        {count === 1
                            ? t("pets_page.trust_h_one")
                            : t("pets_page.trust_h_many", { count })}
                    </h2>
                    <p className="mt-1.5 text-sm text-white/85 max-w-md">
                        {t("pets_page.trust_sub")}
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ------------- Pet card ------------- */
function PetCard({ pet, confirming, onConfirm, onCancelConfirm, onDelete, t }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const nav = useNavigate();
    const url = finderUrl(pet.public_id);

    return (
        <article
            data-testid={`pets-card-${pet.id}`}
            className="group card-soft overflow-hidden flex flex-col hover:shadow-hover hover:-translate-y-0.5 transition-all duration-300"
        >
            <div className="relative aspect-[5/4] bg-paw-muted dark:bg-white/5">
                {pet.photo_path ? (
                    <img
                        src={fileUrl(pet.photo_path)}
                        alt={pet.name}
                        className="pet-photo absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        data-testid={`pets-photo-${pet.id}`}
                    />
                ) : (
                    <div className="absolute inset-0 grid place-items-center text-paw-forest dark:text-paw-terra">
                        <PawPrint size={72} weight="duotone" />
                    </div>
                )}

                <div aria-hidden className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />

                <div data-testid={`pets-protection-${pet.id}`} className="absolute bottom-4 end-4 z-[1]">
                    <div className="relative w-11 h-11">
                        <span className="shield-ring text-paw-terra" />
                        <span className="shield-ring r2 text-paw-terra/75" />
                        <span className="shield-ring r3 text-paw-terra/55" />
                        <div className="shield-core absolute inset-1 rounded-full bg-paw-terra/95 backdrop-blur grid place-items-center ring-2 ring-white/80 dark:ring-white/40">
                            <ShieldCheck size={18} weight="fill" className="text-white heartbeat" />
                        </div>
                    </div>
                    <span data-testid={`pets-protection-${pet.id}-title`} className="sr-only">
                        {t("pets_page.protected_line", { name: pet.name })}
                    </span>
                </div>

                <span
                    data-testid={`pets-status-${pet.id}`}
                    className={`absolute top-3 start-3 inline-flex items-center gap-1.5 rounded-full backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold shadow-soft ${
                        pet.is_lost
                            ? "bg-red-600 text-white ring-1 ring-white/40"
                            : "bg-white/95 dark:bg-paw-ink/85 text-paw-forest dark:text-paw-terra"
                    }`}
                >
                    {pet.is_lost
                        ? (<><Siren size={11} weight="fill" className="animate-pulse" /> {t("pets_page.status_lost")}</>)
                        : (<><ShieldCheck size={11} weight="fill" /> {t("pets_page.status_active")}</>)}
                </span>

                <div className="absolute top-3 end-3">
                    <button
                        type="button"
                        data-testid={`pets-more-${pet.id}`}
                        onClick={() => setMenuOpen((v) => !v)}
                        onBlur={() => setTimeout(() => setMenuOpen(false), 200)}
                        aria-label="More"
                        className="w-9 h-9 rounded-full bg-white/85 dark:bg-paw-ink/70 backdrop-blur grid place-items-center text-paw-ink dark:text-paw-bone shadow-soft hover:bg-white transition"
                    >
                        <DotsThreeVertical size={18} weight="bold" />
                    </button>
                    {menuOpen && (
                        <div className="absolute end-0 mt-2 w-40 rounded-2xl border border-paw-line dark:border-white/10 bg-white dark:bg-paw-ink shadow-hover overflow-hidden z-10">
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); nav(`/pets/${pet.id}/edit`); }}
                                data-testid={`pets-menu-edit-${pet.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition"
                            >
                                <PencilSimple size={14} /> <span>{t("pets_page.act_edit")}</span>
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); onConfirm(); setMenuOpen(false); }}
                                data-testid={`pets-menu-delete-${pet.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                            >
                                <Trash size={14} /> <span>{t("pets_page.act_delete")}</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5">
                    <h3
                        data-testid={`pets-name-${pet.id}`}
                        className="font-display text-white text-2xl sm:text-3xl tracking-tight leading-tight drop-shadow"
                    >
                        {pet.name}
                    </h3>
                    <p className="mt-0.5 text-white/85 text-xs sm:text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{t(`petform.type_${pet.type}`)}</span>
                        {pet.breed && <span>· {pet.breed}</span>}
                        {pet.city && (
                            <span className="inline-flex items-center gap-1">
                                · <MapPin size={11} weight="fill" /> {pet.city}
                            </span>
                        )}
                    </p>
                </div>
            </div>

            <div className="p-4 sm:p-5 flex-1 flex flex-col">
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 flex items-center gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${pet.is_lost ? "bg-red-500 animate-pulse" : "bg-paw-terra paw-pulse"}`} aria-hidden />
                    <span data-testid={`pets-protected-line-${pet.id}`}>
                        {pet.is_lost
                            ? t("pets_page.lost_line", { name: pet.name })
                            : t("pets_page.protected_line", { name: pet.name })}
                    </span>
                    {!pet.is_lost && <span className="opacity-70">· {t("pets_page.status_sub")}</span>}
                </p>

                <div className="mt-3.5 grid grid-cols-3 gap-2" data-testid={`pets-actions-${pet.id}`}>
                    <a
                        href={url}
                        target="_blank" rel="noreferrer"
                        data-testid={`pets-view-profile-${pet.id}`}
                        className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-paw-line dark:border-white/10 py-3 px-2 text-[11px] font-medium text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition text-center leading-tight"
                    >
                        <ArrowUpRight size={18} weight="bold" className="text-paw-forest dark:text-paw-terra" />
                        {t("pets_page.act_view")}
                    </a>
                    <Link
                        to={`/qr/${pet.id}`}
                        data-testid={`pets-qr-${pet.id}`}
                        className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-paw-forest text-white dark:bg-paw-terra py-3 px-2 text-[11px] font-medium hover:opacity-95 transition text-center leading-tight"
                    >
                        <QrCode size={18} weight="fill" />
                        {t("pets_page.act_qr")}
                    </Link>
                    <button
                        type="button"
                        onClick={() => nav(`/pets/${pet.id}/edit`)}
                        data-testid={`pets-edit-${pet.id}`}
                        className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-paw-line dark:border-white/10 py-3 px-2 text-[11px] font-medium text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition text-center leading-tight"
                    >
                        <PencilSimple size={18} weight="fill" className="text-paw-forest dark:text-paw-terra" />
                        {t("pets_page.act_edit")}
                    </button>
                </div>

                <Link
                    to={`/pets/${pet.id}/safety`}
                    data-testid={`pets-safety-${pet.id}`}
                    className={`group mt-3 flex items-center gap-3 rounded-2xl px-4 py-3 transition ${
                        pet.is_lost
                            ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-hover"
                            : "border border-paw-line dark:border-white/10 bg-white dark:bg-white/5 hover:border-paw-forest/40 dark:hover:border-paw-terra/40"
                    }`}
                >
                    <div className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${
                        pet.is_lost
                            ? "bg-white/15 text-white"
                            : "bg-paw-forest/10 text-paw-forest dark:bg-paw-terra/15 dark:text-paw-terra"
                    }`}>
                        {pet.is_lost
                            ? <Siren size={18} weight="fill" className="animate-pulse" />
                            : <ShieldStar size={18} weight="fill" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${pet.is_lost ? "text-white" : "text-paw-ink dark:text-paw-bone"}`}>
                            {pet.is_lost ? t("pets_page.safety_lost_h", { name: pet.name }) : t("pets_page.safety_h")}
                        </p>
                        <p className={`text-[11px] truncate ${pet.is_lost ? "text-white/80" : "text-paw-ink2 dark:text-paw-bone/60"}`}>
                            {pet.is_lost ? t("pets_page.safety_lost_sub") : t("pets_page.safety_sub")}
                        </p>
                    </div>
                    <ArrowUpRight size={16} weight="bold" className={pet.is_lost ? "text-white" : "text-paw-forest dark:text-paw-terra"} />
                </Link>

                {confirming && (
                    <div
                        data-testid={`pets-confirm-delete-${pet.id}`}
                        className="mt-4 rounded-2xl bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40 p-3"
                    >
                        <p className="text-sm text-red-700 dark:text-red-300">
                            {t("pets_page.confirm_delete", { name: pet.name })}
                        </p>
                        <div className="mt-2 flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={onCancelConfirm}
                                data-testid={`pets-confirm-cancel-${pet.id}`}
                                className="px-3 py-1.5 rounded-full text-xs font-medium text-paw-ink2 dark:text-paw-bone/60 hover:bg-white dark:hover:bg-white/5 transition"
                            >
                                {t("pets_page.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={onDelete}
                                data-testid={`pets-confirm-delete-btn-${pet.id}`}
                                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition"
                            >
                                {t("pets_page.act_delete")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

/* ------------- Empty state ------------- */
function EmptyPets({ onCreate, t }) {
    return (
        <div
            data-testid="pets-empty"
            className="relative rounded-3xl overflow-hidden border border-paw-line dark:border-white/10 bg-white dark:bg-[hsl(152,12%,10%)] p-8 sm:p-14 text-center"
        >
            <div aria-hidden className="absolute -top-20 -end-16 w-64 h-64 rounded-full bg-paw-terra/12 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -start-16 w-64 h-64 rounded-full bg-paw-forest/8 blur-3xl" />
            <div className="relative">
                <div className="inline-grid place-items-center w-16 h-16 rounded-2xl bg-paw-forest text-white mb-4 shadow-hover">
                    <PawPrint size={30} weight="fill" />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-paw-ink dark:text-paw-bone">{t("pets_page.empty_h")}</h2>
                <p className="mt-2 text-sm sm:text-base text-paw-ink2 dark:text-paw-bone/60 max-w-md mx-auto">{t("pets_page.empty_sub")}</p>
                <button onClick={onCreate} data-testid="pets-empty-create" className="mt-6 btn-primary">
                    <Plus size={16} /> <span className="ms-1.5">{t("pets_page.empty_cta")}</span>
                </button>
            </div>
        </div>
    );
}

/* ------------- Skeletons ------------- */
function PetSkeletons() {
    return (
        <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="pets-loading">
            {[1, 2, 3].map((i) => (
                <div key={i} className="card-soft overflow-hidden">
                    <div className="aspect-[5/4] bg-paw-muted dark:bg-white/5 animate-pulse" />
                    <div className="p-5 space-y-3">
                        <div className="h-4 w-1/2 rounded-full bg-paw-muted dark:bg-white/5 animate-pulse" />
                        <div className="h-3 w-3/4 rounded-full bg-paw-muted dark:bg-white/5 animate-pulse" />
                        <div className="h-10 w-full rounded-2xl bg-paw-muted dark:bg-white/5 animate-pulse" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ------------- Error state ------------- */
function PetsErrorState({ onRetry, t }) {
    return (
        <div
            data-testid="pets-error-state"
            className="rounded-3xl border border-rose-500/25 bg-rose-500/8 dark:bg-rose-500/10 p-8 sm:p-10 text-center"
        >
            <div className="mx-auto w-14 h-14 rounded-2xl bg-rose-500/15 grid place-items-center">
                <Siren size={26} weight="fill" className="text-rose-500" />
            </div>
            <h3 className="mt-5 font-display text-2xl text-paw-ink dark:text-white leading-tight tracking-tight">
                {t("pets_page.error_title")}
            </h3>
            <p className="mt-2 text-sm text-paw-ink2 dark:text-white/65 leading-relaxed max-w-md mx-auto">
                {t("pets_page.error_sub")}
            </p>
            <button
                type="button"
                onClick={onRetry}
                data-testid="pets-error-retry"
                className="btn-primary mt-6 text-sm mx-auto"
            >
                {t("pets_page.error_retry")}
            </button>
        </div>
    );
}

